import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { VoucherDiscountTypeEnum } from '@/graphql/generated';

import {
	CUSTOMERS_QUERY,
	FACILITIES_QUERY,
	CAMPAIGNS_QUERY
} from './vouchers.gql';

export interface CustomerOption {
	customerId: string;
	firmName: string;
	email?: string | null;
}

export interface VoucherDiscountTypeOption {
	voucherDiscountTypeEnum: VoucherDiscountTypeEnum;
	name: string;
	description?: string | null;
}

export interface FacilityOption {
	facilityId: string;
	name: string;
}

export interface CampaignOption {
	campaignId: string;
	name: string;
	description?: string | null;
}

interface CustomersQueryResponse {
	customers?: {
		nodes?: Array<{
			customerId: string;
			firmName?: string | null;
			email?: string | null;
		}> | null;
	} | null;
}

interface FacilitiesQueryResponse {
	facilities?: {
		nodes?: Array<{
			facilityId: string;
			name?: string | null;
		}> | null;
	} | null;
}

interface CampaignsQueryResponse {
	campaigns?: {
		nodes?: Array<{
			campaignId: string;
			name?: string | null;
			description?: string | null;
		}> | null;
	} | null;
}

export const useCustomerOptions = () => {
	const { data, loading, error } = useQuery<CustomersQueryResponse>(
		CUSTOMERS_QUERY,
		{
			fetchPolicy: 'cache-first'
		}
	);

	const customerOptions = useMemo<CustomerOption[]>(() => {
		if (!data?.customers?.nodes) {
			return [];
		}

		return data.customers.nodes
			.filter(Boolean)
			.map(node => ({
				customerId: node.customerId,
				firmName: node.firmName ?? 'Unnamed Customer',
				email: node.email ?? null
			}))
			.sort((a, b) => a.firmName.localeCompare(b.firmName));
	}, [data?.customers?.nodes]);

	return {
		customerOptions,
		loading,
		error
	};
};

// Fallback discount types based on common voucher types
const FALLBACK_DISCOUNT_TYPES: VoucherDiscountTypeOption[] = [
	{ voucherDiscountTypeEnum: VoucherDiscountTypeEnum.TariffReplacement, name: 'Tariff replacement', description: 'Replace the entire tariff cost' },
	{ voucherDiscountTypeEnum: VoucherDiscountTypeEnum.FixedAmount, name: 'Fixed amount', description: 'Subtract a fixed amount from the total' },
	{ voucherDiscountTypeEnum: VoucherDiscountTypeEnum.PercentageDiscount, name: 'Percentage discount', description: 'Apply a percentage discount to the total' },
	{ voucherDiscountTypeEnum: VoucherDiscountTypeEnum.FreeTime, name: 'Free time', description: 'Grant free parking time in minutes' }
];

export const useVoucherDiscountTypeOptions = () => {
	// Use hardcoded discount types to avoid GraphQL complexity issues
	// The server query was causing a 400 error due to exceeding maximum type cost
	const voucherDiscountTypeOptions = useMemo<VoucherDiscountTypeOption[]>(() => {
		return FALLBACK_DISCOUNT_TYPES;
	}, []);

	return {
		voucherDiscountTypeOptions,
		loading: false,
		error: null
	};
};

export const useFacilityOptions = () => {
	const { data, loading, error } = useQuery<FacilitiesQueryResponse>(
		FACILITIES_QUERY,
		{
			fetchPolicy: 'cache-first'
		}
	);

	const facilityOptions = useMemo<FacilityOption[]>(() => {
		if (!data?.facilities?.nodes) {
			return [];
		}

		return data.facilities.nodes
			.filter(Boolean)
			.map(node => ({
				facilityId: node.facilityId,
				name: node.name ?? 'Unnamed Facility'
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [data?.facilities?.nodes]);

	return {
		facilityOptions,
		loading,
		error
	};
};

export const useCampaignOptions = () => {
	const { data, loading, error } = useQuery<CampaignsQueryResponse>(
		CAMPAIGNS_QUERY,
		{
			fetchPolicy: 'cache-first'
		}
	);

	const campaignOptions = useMemo<CampaignOption[]>(() => {
		if (!data?.campaigns?.nodes) {
			return [];
		}

		return data.campaigns.nodes
			.filter(Boolean)
			.map(node => ({
				campaignId: node.campaignId,
				name: node.name ?? 'Unnamed Campaign',
				description: node.description
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [data?.campaigns?.nodes]);

	return {
		campaignOptions,
		loading,
		error
	};
};
